// projectWizard/ProjectWizard.jsx
//
// One component serves BOTH Create Project and Edit Project. That is the
// point: previously there was only a create dialog, and the edit button was
// inert with no form behind it at all. Everything below is mode-driven —
// `mode="edit"` adds a fetch + hydrate and switches the submit verb from
// POST to PUT. No other behaviour differs, so the two screens cannot drift.
//
// Backend contract is untouched:
//   create  POST /api/nextgen-builder/projects
//   read    GET  /api/nextgen-builder/projects/:id
//   update  PUT  /api/nextgen-builder/projects/:id      (already routed, app.js:881)
// The payload is built by toApiPayload() from an explicit allow-list because
// updateProject derives its SQL SET clause from the body's keys.
//
// Rendering: only the active step is mounted. Steps are memoised and the
// field setter identity is stable, so typing in step 1 does not re-render
// the other eight.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import useMediaQuery from '@mui/material/useMediaQuery';

import API_BASE_URL from '../../../Config/config';
import { BRAND } from './WizardControls';
import { WIZARD_STEPS, fromApiProject, toApiPayload } from './projectFormModel';
import useProjectForm, { clearProjectDraft } from './useProjectForm';
import ProjectBasicInfo from './steps/ProjectBasicInfo';
import ProjectLearning from './steps/ProjectLearning';
import GenericStep from './steps/GenericStep';

const BESPOKE_STEPS = { basic: ProjectBasicInfo, learning: ProjectLearning };

const StepRail = React.memo(function StepRail({ current, onGo, stepErrors, compact }) {
  return (
    <Box
      role="tablist"
      aria-label="Wizard steps"
      sx={{
        display: 'flex',
        flexDirection: compact ? 'row' : 'column',
        gap: compact ? 1 : 0.5,
        overflowX: compact ? 'auto' : 'visible',
        pb: compact ? 1 : 0,
      }}
    >
      {WIZARD_STEPS.map((step, i) => {
        const active = i === current;
        const invalid = !!stepErrors[step.id];
        return (
          <Box
            key={step.id}
            role="tab"
            tabIndex={0}
            aria-selected={active}
            onClick={() => onGo(i)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onGo(i); }
            }}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.25, px: 1.75, py: 1.25, cursor: 'pointer',
              borderRadius: '12px', whiteSpace: 'nowrap', flexShrink: 0,
              bgcolor: active ? BRAND.primarySoft : 'transparent',
              '&:hover': { bgcolor: active ? BRAND.primarySoft : '#f1f5f9' },
              '&:focus-visible': { outline: `2px solid ${BRAND.primary}`, outlineOffset: 2 },
            }}
          >
            <Box
              aria-hidden="true"
              sx={{
                width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 700,
                bgcolor: active ? BRAND.primary : invalid ? '#fee2e2' : '#e2e8f0',
                color: active ? '#fff' : invalid ? BRAND.danger : BRAND.muted,
              }}
            >
              {invalid && !active ? '!' : i + 1}
            </Box>
            <Typography
              sx={{
                fontSize: 13.5,
                fontWeight: active ? 700 : 500,
                color: active ? BRAND.primaryDark : BRAND.text,
              }}
            >
              {step.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
});

const ProjectWizard = ({ open, mode = 'create', projectId = null, techStacks = [], onClose, onSaved }) => {
  const isEdit = mode === 'edit';
  const isMobile = useMediaQuery('(max-width:900px)');

  const [stepIndex, setStepIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const form = useProjectForm({ draftId: isEdit ? String(projectId) : 'new' });
  const { values, dirty, completion, stepErrors, setField, hydrate, markSaved, errorsForStep, draftSavedAt } = form;

  const step = WIZARD_STEPS[stepIndex];
  const currentErrors = useMemo(() => errorsForStep(step.id), [errorsForStep, step.id]);

  // ── Load the project being edited ─────────────────────────────────────────
  // This is the piece that never existed. Without it the form renders its
  // blank defaults, which is exactly what "existing data is not loading"
  // described.
  useEffect(() => {
    if (!open) return;
    setStepIndex(0);
    setLoadError('');
    if (!isEdit || !projectId) {
      hydrate({});
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/nextgen-builder/projects/${projectId}`);
        if (cancelled) return;
        if (res.data && res.data.success && res.data.data) {
          hydrate(fromApiProject(res.data.data));
        } else {
          setLoadError((res.data && res.data.message) || 'Could not load this project.');
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(
            (err.response && err.response.data && err.response.data.message) ||
            'Could not load this project. Check your connection and try again.'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, isEdit, projectId, hydrate]);

  const goTo = useCallback((i) => {
    setStepIndex(Math.max(0, Math.min(WIZARD_STEPS.length - 1, i)));
  }, []);

  const handleNext = useCallback(() => {
    const errs = errorsForStep(WIZARD_STEPS[stepIndex].id);
    if (Object.keys(errs).length) {
      setToast({ open: true, message: 'Fix the highlighted fields before continuing.', severity: 'warning' });
      return;
    }
    goTo(stepIndex + 1);
  }, [errorsForStep, stepIndex, goTo]);

  const submit = useCallback(async () => {
    const blocking = Object.keys(stepErrors);
    if (blocking.length) {
      const firstBad = WIZARD_STEPS.findIndex((s) => s.id === blocking[0]);
      if (firstBad >= 0) goTo(firstBad);
      setToast({ open: true, message: 'Some required fields still need attention.', severity: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const payload = toApiPayload(values, { techStacks });
      const res = isEdit
        ? await axios.put(`${API_BASE_URL}/api/nextgen-builder/projects/${projectId}`, payload)
        : await axios.post(`${API_BASE_URL}/api/nextgen-builder/projects`, payload);

      if (res.data && res.data.success) {
        markSaved(values);
        clearProjectDraft(isEdit ? String(projectId) : 'new');
        setToast({
          open: true,
          message: isEdit ? 'Project updated successfully' : 'Project created successfully',
          severity: 'success',
        });
        // Hand the fresh record back so the details page can refresh in
        // place — no page reload, per the brief.
        if (typeof onSaved === 'function') onSaved((res.data && res.data.data) || null);
      } else {
        setToast({
          open: true,
          message: (res.data && res.data.message) || 'Save failed. Your changes are still here — try again.',
          severity: 'error',
        });
      }
    } catch (err) {
      setToast({
        open: true,
        message:
          (err.response && err.response.data && err.response.data.message) ||
          'Save failed. Your changes have not been lost — check your connection and try again.',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  }, [stepErrors, values, techStacks, isEdit, projectId, markSaved, onSaved, goTo]);

  const saveDraft = useCallback(() => {
    setField('status', 'draft');
    setToast({ open: true, message: 'Marked as draft — remember to save.', severity: 'info' });
  }, [setField]);

  const requestClose = useCallback(() => {
    if (dirty) {
      // eslint-disable-next-line no-alert
      const leave = window.confirm('You have unsaved changes. Close and discard them?');
      if (!leave) return;
    }
    if (typeof onClose === 'function') onClose();
  }, [dirty, onClose]);

  const StepComponent = BESPOKE_STEPS[step.id];
  const isLast = stepIndex === WIZARD_STEPS.length - 1;

  return (
    <>
      <Dialog
        open={!!open}
        onClose={requestClose}
        fullScreen
        aria-labelledby="wizard-title"
        PaperProps={{ sx: { bgcolor: BRAND.canvas } }}
      >
        {/* ── Sticky header ─────────────────────────────────────────────── */}
        <Box
          sx={{
            position: 'sticky', top: 0, zIndex: 10, bgcolor: BRAND.surface,
            borderBottom: `1px solid ${BRAND.border}`, px: { xs: 2, md: 4 }, pt: 2.5, pb: 0,
          }}
        >
          <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
              <Box sx={{ minWidth: 0 }}>
                <Typography id="wizard-title" sx={{ fontSize: { xs: 22, md: 28 }, fontWeight: 800, color: BRAND.text, lineHeight: 1.2 }}>
                  {isEdit ? 'Edit Project' : 'Create Project'}
                </Typography>
                <Typography sx={{ fontSize: 14, color: BRAND.muted, mt: 0.5 }}>
                  Build a complete learning project for students.
                </Typography>
              </Box>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ flexShrink: 0 }}>
                <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
                  <Typography sx={{ fontSize: 12, color: BRAND.muted, fontWeight: 600, letterSpacing: .3 }}>PROGRESS</Typography>
                  <Typography sx={{ fontSize: 13.5, color: BRAND.text, fontWeight: 700 }}>
                    Step {stepIndex + 1} of {WIZARD_STEPS.length} · {completion}%
                  </Typography>
                </Box>
                <IconButton onClick={requestClose} aria-label="Close" sx={{ border: `1px solid ${BRAND.border}`, borderRadius: '10px' }}>✕</IconButton>
              </Stack>
            </Stack>

            <LinearProgress
              variant="determinate"
              value={((stepIndex + 1) / WIZARD_STEPS.length) * 100}
              sx={{
                mt: 2, height: 6, borderRadius: 3, bgcolor: '#eef2f7',
                '& .MuiLinearProgress-bar': { bgcolor: BRAND.primary, borderRadius: 3 },
              }}
            />

            {isMobile ? (
              <Box sx={{ mt: 1.5 }}>
                <StepRail current={stepIndex} onGo={goTo} stepErrors={stepErrors} compact />
              </Box>
            ) : <Box sx={{ height: 12 }} />}
          </Box>
        </Box>

        {/* ── Body ──────────────────────────────────────────────────────── */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 2, md: 4 }, py: 3 }}>
          <Box
            sx={{
              maxWidth: 1400, mx: 'auto',
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '260px minmax(0, 1fr)' },
              gap: { xs: 0, md: 4 },
              alignItems: 'start',
            }}
          >
            {!isMobile ? (
              <Box sx={{ position: 'sticky', top: 16 }}>
                <StepRail current={stepIndex} onGo={goTo} stepErrors={stepErrors} />
              </Box>
            ) : null}

            <Box sx={{ minWidth: 0, pb: 12 }}>
              {loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10, gap: 2 }}>
                  <CircularProgress sx={{ color: BRAND.primary }} />
                  <Typography sx={{ color: BRAND.muted, fontSize: 14 }}>Loading project…</Typography>
                </Box>
              ) : loadError ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography sx={{ fontSize: 17, fontWeight: 700, color: BRAND.text, mb: 1 }}>
                    We could not load this project
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: BRAND.muted, mb: 3 }}>{loadError}</Typography>
                  <Button variant="contained" onClick={() => setStepIndex((i) => i)} sx={{ borderRadius: '12px', textTransform: 'none', bgcolor: BRAND.primary }}>
                    Try again
                  </Button>
                </Box>
              ) : StepComponent ? (
                <StepComponent values={values} errors={currentErrors} setField={setField} />
              ) : (
                <GenericStep step={step} values={values} errors={currentErrors} setField={setField} />
              )}
            </Box>
          </Box>
        </Box>

        {/* ── Sticky footer ─────────────────────────────────────────────── */}
        <Box
          sx={{
            position: 'sticky', bottom: 0, zIndex: 10, bgcolor: BRAND.surface,
            borderTop: `1px solid ${BRAND.border}`, px: { xs: 2, md: 4 }, py: 2,
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            sx={{ maxWidth: 1400, mx: 'auto' }}
          >
            <Typography sx={{ fontSize: 12.5, color: BRAND.muted, order: { xs: 2, sm: 1 } }}>
              {saving ? 'Saving…' : dirty ? (draftSavedAt ? 'Draft saved locally' : 'Unsaved changes') : 'All changes saved'}
            </Typography>
            <Stack direction="row" spacing={1.5} sx={{ order: { xs: 1, sm: 2 } }}>
              <Button
                onClick={() => goTo(stepIndex - 1)}
                disabled={stepIndex === 0 || saving}
                sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 600, color: BRAND.muted, px: 2.5 }}
              >
                Previous
              </Button>
              <Button
                variant="outlined"
                onClick={saveDraft}
                disabled={saving}
                sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 600, borderColor: BRAND.border, color: BRAND.primaryDark, px: 2.5 }}
              >
                Save Draft
              </Button>
              {isLast ? (
                <Button
                  variant="contained"
                  onClick={submit}
                  disabled={saving}
                  sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700, bgcolor: BRAND.primary, px: 3, '&:hover': { bgcolor: BRAND.primaryDark } }}
                >
                  {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Publish'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={saving}
                  sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700, bgcolor: BRAND.primary, px: 3, '&:hover': { bgcolor: BRAND.primaryDark } }}
                >
                  Next
                </Button>
              )}
            </Stack>
          </Stack>
        </Box>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} variant="filled" onClose={() => setToast((t) => ({ ...t, open: false }))} sx={{ borderRadius: '12px' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProjectWizard;
